import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { Navbar } from '@/components/layout/Navbar';
import { Button } from '@/components/ui/Button';
import { BookOpenIcon, PuzzlePieceIcon } from '@heroicons/react/24/solid';
import { playTapDown, playSwish } from '@/lib/hooks/useSounds';

type Panel = 'courses' | 'join';

const EASE     = '650ms cubic-bezier(0.22, 1, 0.36, 1)';
const EASE_MED = '450ms cubic-bezier(0.22, 1, 0.36, 1)';

const NOISE = `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")`;

function useIsDesktop() {
  const [isDesktop, setIsDesktop] = useState(() => window.matchMedia('(min-width: 768px)').matches);
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 768px)');
    const handler = (e: MediaQueryListEvent) => setIsDesktop(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);
  return isDesktop;
}

export function HomePage() {
  const [hovered, setHovered] = useState<Panel | null>(null);
  const [retreating, setRetreating] = useState<Panel | null>(null);
  const isDesktop = useIsDesktop();

  const coursesActive = hovered === 'courses';
  const joinActive    = hovered === 'join';

  function handleLeave(panel: Panel) {
    setHovered(prev => (prev === panel ? null : prev));
    setRetreating(panel);
    window.setTimeout(() => {
      setRetreating(prev => (prev === panel ? null : prev));
    }, 700);
  }

  function desktopPanelStyle(panel: Panel): React.CSSProperties {
    const active = hovered === panel;
    const isRetreating = retreating === panel && !active;
    const shadow = panel === 'courses'
      ? '8px 0 28px 0px rgba(0,0,0,0.45)'
      : '-8px 0 28px 0px rgba(0,0,0,0.45)';
    const BLEED = 2;
    return {
      position: 'absolute',
      top: 0, bottom: 0,
      ...(panel === 'courses' ? { left: -BLEED } : { right: -BLEED }),
      width: `calc(${active ? '52%' : '50%'} + ${BLEED}px)`,
      zIndex: active ? 3 : isRetreating ? 2 : 1,
      overflow: 'hidden',
      transition: `width ${EASE}, box-shadow ${EASE_MED}`,
      boxShadow: active ? shadow : '0 0 0 0 rgba(0,0,0,0)',
    };
  }

  function mobilePanelStyle(): React.CSSProperties {
    return {
      position: 'relative',
      width: '100%',
      minHeight: '50dvh',
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
    };
  }

  function panelStyle(panel: Panel): React.CSSProperties {
    return isDesktop ? desktopPanelStyle(panel) : mobilePanelStyle();
  }

  function panelClass(panel: Panel): string {
    const entry = isDesktop
      ? (panel === 'courses' ? 'home-panel-l' : 'home-panel-r')
      : (panel === 'courses' ? 'home-fade-l'  : 'home-fade-r');
    return `group flex flex-col items-center justify-center ${entry}`;
  }

  const raisedBtnClass = 'btn-raised-from-group';

  return (
    <div style={{ minHeight: '100dvh', display: 'flex', flexDirection: 'column' }}>
      <Navbar transparent />

      <div style={{ flex: 1, position: 'relative', overflow: 'hidden' }}>

        {/* ── Courses ── */}
        <Link
          to="/courses"
          onMouseEnter={() => { setHovered('courses'); playSwish(); }}
          onMouseLeave={() => handleLeave('courses')}
          onPointerDown={() => playTapDown()}
          className={panelClass('courses')}
          style={panelStyle('courses')}
        >
          <div className="absolute inset-0" style={{
            background: 'linear-gradient(145deg, #0a3319 0%, #1e7a46 45%, #3ca067 75%, #5aba84 100%)',
            transform: coursesActive ? 'scale(1.08)' : 'scale(1)',
            filter: coursesActive ? 'brightness(1.1)' : 'brightness(1)',
            transition: `transform ${EASE}, filter ${EASE_MED}`,
          }} />
          <div className="absolute inset-0" style={{
            opacity: coursesActive ? 0.32 : 0.16,
            background: 'radial-gradient(ellipse 70% 70% at 15% 110%, rgba(255,255,255,0.4) 0%, transparent 55%), radial-gradient(ellipse 50% 50% at 85% -5%, rgba(255,255,255,0.2) 0%, transparent 50%)',
            transition: `opacity ${EASE_MED}`,
          }} />
          <div className="absolute inset-0 opacity-[0.07]" style={{ backgroundImage: NOISE, backgroundSize: '200px 200px' }} />
          <div className="absolute inset-0 pointer-events-none" style={{
            background: 'linear-gradient(110deg, transparent 30%, rgba(255,255,255,0.05) 50%, transparent 70%)',
            transform: coursesActive ? 'translateX(100%)' : 'translateX(-100%)',
            transition: 'transform 900ms cubic-bezier(0.22, 1, 0.36, 1)',
          }} />
          <div className="absolute inset-0" style={{
            background: 'radial-gradient(ellipse 90% 90% at 50% 50%, transparent 50%, rgba(0,0,0,0.22) 100%)',
            opacity: coursesActive ? 0 : 1,
            transition: `opacity ${EASE_MED}`,
          }} />

          <div
            className="relative z-10 flex flex-col items-center gap-5 px-8 text-center"
            style={{
              transform: coursesActive ? 'translateY(-6px) scale(1.04)' : 'translateY(0) scale(1)',
              transition: `transform ${EASE}`,
            }}
          >
            <div className="home-icon-l">
              <div style={{
                transform: coursesActive ? 'scale(1.15) translateY(-3px)' : 'scale(1) translateY(0)',
                borderRadius: '1rem', padding: '1rem',
                background: coursesActive ? 'rgba(255,255,255,0.2)' : 'rgba(255,255,255,0.11)',
                backdropFilter: 'blur(8px)',
                boxShadow: coursesActive
                  ? '0 8px 32px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.3)'
                  : '0 2px 8px rgba(0,0,0,0.12), inset 0 1px 0 rgba(255,255,255,0.2)',
                transition: `transform ${EASE}, background ${EASE_MED}, box-shadow ${EASE_MED}`,
              }}>
                <BookOpenIcon className="h-12 w-12 text-white" />
              </div>
            </div>

            <div className="home-text-l">
              <h2 className="font-serif font-bold text-white tracking-tight"
                style={{ fontSize: 'clamp(2.5rem, 5vw, 4rem)' }}>
                Courses
              </h2>
              <p className="mt-2 font-sans" style={{
                color: `rgba(255,255,255,${coursesActive ? 0.85 : 0.6})`,
                transition: `color ${EASE_MED}`,
              }}>
                Browse our learning library
              </p>
            </div>

            <div className="home-pill-l">
              <Button
                variant="raised"
                tabIndex={-1}
                aria-hidden
                className={`${raisedBtnClass} !bg-[#3ca067] hover:!bg-[#3ca067]`}
                style={{ ['--btn-depth' as string]: '#1e7a46' }}
              >
                Explore
              </Button>
            </div>
          </div>
        </Link>

        {/* ── Join a Game ── */}
        <Link
          to="/join"
          onMouseEnter={() => { setHovered('join'); playSwish(); }}
          onMouseLeave={() => handleLeave('join')}
          onPointerDown={() => playTapDown()}
          className={panelClass('join')}
          style={panelStyle('join')}
        >
          <div className="absolute inset-0" style={{
            background: 'linear-gradient(145deg, #130f3a 0%, #3730a3 45%, #6d28d9 75%, #8b5cf6 100%)',
            transform: joinActive ? 'scale(1.08)' : 'scale(1)',
            filter: joinActive ? 'brightness(1.1)' : 'brightness(1)',
            transition: `transform ${EASE}, filter ${EASE_MED}`,
          }} />
          <div className="absolute inset-0" style={{
            opacity: joinActive ? 0.32 : 0.16,
            background: 'radial-gradient(ellipse 70% 70% at 85% 110%, rgba(255,255,255,0.4) 0%, transparent 55%), radial-gradient(ellipse 50% 50% at 15% -5%, rgba(255,255,255,0.2) 0%, transparent 50%)',
            transition: `opacity ${EASE_MED}`,
          }} />
          <div className="absolute inset-0 opacity-[0.07]" style={{ backgroundImage: NOISE, backgroundSize: '200px 200px' }} />
          <div className="absolute inset-0 pointer-events-none" style={{
            background: 'linear-gradient(110deg, transparent 30%, rgba(255,255,255,0.05) 50%, transparent 70%)',
            transform: joinActive ? 'translateX(100%)' : 'translateX(-100%)',
            transition: 'transform 900ms cubic-bezier(0.22, 1, 0.36, 1)',
          }} />
          <div className="absolute inset-0" style={{
            background: 'radial-gradient(ellipse 90% 90% at 50% 50%, transparent 50%, rgba(0,0,0,0.22) 100%)',
            opacity: joinActive ? 0 : 1,
            transition: `opacity ${EASE_MED}`,
          }} />

          <div
            className="relative z-10 flex flex-col items-center gap-5 px-8 text-center"
            style={{
              transform: joinActive ? 'translateY(-6px) scale(1.04)' : 'translateY(0) scale(1)',
              transition: `transform ${EASE}`,
            }}
          >
            <div className="home-icon-r">
              <div style={{
                transform: joinActive ? 'scale(1.15) translateY(-3px)' : 'scale(1) translateY(0)',
                borderRadius: '1rem', padding: '1rem',
                background: joinActive ? 'rgba(255,255,255,0.2)' : 'rgba(255,255,255,0.11)',
                backdropFilter: 'blur(8px)',
                boxShadow: joinActive
                  ? '0 8px 32px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.3)'
                  : '0 2px 8px rgba(0,0,0,0.12), inset 0 1px 0 rgba(255,255,255,0.2)',
                transition: `transform ${EASE}, background ${EASE_MED}, box-shadow ${EASE_MED}`,
              }}>
                <PuzzlePieceIcon className="h-12 w-12 text-white" />
              </div>
            </div>

            <div className="home-text-r">
              <h2 className="font-serif font-bold text-white tracking-tight"
                style={{ fontSize: 'clamp(2.5rem, 5vw, 4rem)' }}>
                Join a Game
              </h2>
              <p className="mt-2 font-sans" style={{
                color: `rgba(255,255,255,${joinActive ? 0.85 : 0.6})`,
                transition: `color ${EASE_MED}`,
              }}>
                Enter a code to play live
              </p>
            </div>

            <div className="home-pill-r">
              <Button
                variant="raised"
                tabIndex={-1}
                aria-hidden
                className={`${raisedBtnClass} !bg-[#6d28d9] hover:!bg-[#6d28d9]`}
                style={{ ['--btn-depth' as string]: '#3730a3' }}
              >
                Join now
              </Button>
            </div>
          </div>
        </Link>

      </div>
    </div>
  );
}
